import axios from "axios";

const state = {
  mtcLabUsers: null,
  mtcLabFiles: [],
  mtcLabPagination: null,
  workQueue: { buckets: { new: [], accepted: [], processing: [], completed: [] }, counts: {} },
};

const mutations = {
  setMtcLabUsers(state, array) {
    state.mtcLabUsers = array;
  },
  setMtcLabFiles(state, files) {
    state.mtcLabFiles = files;
  },
  setMtcLabPagination(state, pagination) {
    state.mtcLabPagination = pagination;
  },
  updateMtcLabUserInfo(state, user) {
    if (!state.mtcLabUsers) return;
    let index = state.mtcLabUsers.findIndex(u => u.id === user.id);
    if (index !== -1) {
      state.mtcLabUsers.splice(index, 1, user);
    }
  },
  setWorkQueue(state, data) {
    state.workQueue = data;
  }
};

const actions = {
  getMtcLabInfo({ commit }) {
    commit("adminLogined", true);
  },
  getMtcLabUserInfoList({ commit }, data) {
    let url = "/v1/admin/mtcLabUsers?";
    if (data) {
      url += `page=1`;
    }
    return axios.get(url).then(res => {
      commit("setMtcLabUsers", res.data.data.data);
      commit("setMtcLabPagination", {
        total: res.data.data.total,
        lastPage: res.data.data.lastPage,
        page: res.data.data.page,
        perPage: res.data.data.perPage,
      });
    });
  },
  AddMtcLabUser({ commit }, data) {
    return axios.post("/v1/admin/mtcLabUsers", data);
  },
  EditMtcLabUser({ commit }, data) {
    return axios.put(`/v1/admin/mtcLabUsers/${data.id}`, data.form).then(res => {
      commit("updateMtcLabUserInfo", res.data.data.user);
    });
  },
  removeMtcLabUser({ commit }, id) {
    return axios.delete(`/v1/admin/mtcLabUsers/${id}`);
  },
  /*
   * FASTQ uploads go directly from the browser to DigitalOcean Spaces using
   * presigned multipart URLs. The file never passes through the API container,
   * which is what previously made this fail: a 1.5GB body had to survive the
   * ingress body-size limit and three layers of proxy timeouts, while aws-sdk
   * buffered parts inside a memory-capped pod until it was OOM-killed. The
   * browser reported that as a CORS error because a killed pod returns no
   * response for the preflight to read.
   *
   * Per file: ask the API to open a multipart upload, PUT each chunk straight
   * to Spaces, then hand the ETags back so the API can finalise the object and
   * record it. A dropped connection now costs one part, not the whole file.
   *
   * The part PUTs use bare XMLHttpRequest rather than axios on purpose —
   * axios.defaults carries this app's Authorization header and baseURL, and
   * sending either to Spaces breaks the presigned signature.
   */
  uploadMtcLabFiles({ commit }, { kit_id, files, file_meta, onProgress, onFileProgress }) {
    const items = files.map((file, i) => ({
      file,
      meta: (Array.isArray(file_meta) && file_meta[i]) || {}
    }));

    const totalBytes = items.reduce((sum, it) => sum + (it.file.size || 0), 0) || 1;
    const loadedBytes = new Array(items.length).fill(0);

    const reportProgress = () => {
      if (typeof onProgress !== "function") return;
      const loaded = loadedBytes.reduce((a, b) => a + b, 0);
      onProgress(Math.min(100, Math.round((loaded / totalBytes) * 100)));
    };

    // PUT one chunk to Spaces and resolve with its ETag.
    const putPart = (url, blob, onBytes) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url, true);

        xhr.upload.onprogress = e => {
          if (e.lengthComputable) onBytes(e.loaded);
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Requires the Space to expose ETag via CORS ExposeHeaders.
            const etag = xhr.getResponseHeader("ETag");
            if (!etag) {
              reject(
                new Error(
                  "Spaces did not return a readable ETag. The bucket's CORS " +
                  "rule needs ETag in ExposeHeaders."
                )
              );
              return;
            }
            resolve(etag);
          } else {
            reject(new Error(`Part upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error("Part upload failed (network)"));
        xhr.onabort = () => reject(new Error("Part upload aborted"));
        xhr.send(blob);
      });

    const uploadOne = async (item, i) => {
      const { file, meta } = item;

      const created = await axios.post("/v1/mtc_lab/upload/multipart/create", {
        kit_id,
        file_name: file.name,
        file_type: meta.file_type,
        sample_index: meta.sample_index,
        file_size: file.size,
        content_type: file.type || "application/octet-stream"
      });

      const { key, upload_id: uploadId, part_size: partSize } =
        (created.data && created.data.data) || {};

      if (!key || !uploadId || !partSize) {
        throw new Error("Could not start the upload — the server did not return an upload id.");
      }

      // The API tells us up front whether the Space will accept browser PUTs.
      // Failing here gives the real reason instead of a wall of opaque CORS
      // errors once the part uploads start.
      const created_data = (created.data && created.data.data) || {};
      if (created_data.cors_ready === false) {
        throw new Error(
          "The storage bucket is not configured to accept uploads from this " +
          "site yet" +
          (created_data.cors_error ? ` (${created_data.cors_error})` : "") +
          ". Run scripts/set_spaces_cors.js on the API, or give the Spaces " +
          "key permission to change bucket settings."
        );
      }

      const partCount = Math.max(1, Math.ceil(file.size / partSize));

      // Sign each part immediately before it is sent, and again on every retry.
      // Signing the whole file up front gave every URL the same 1-hour expiry,
      // so a slow connection would fail its tail parts with SignatureDoesNotMatch
      // and then retry the same already-expired URL three times.
      const signPart = async partNumber => {
        const signed = await axios.post("/v1/mtc_lab/upload/multipart/sign", {
          key,
          upload_id: uploadId,
          part_numbers: [partNumber]
        });
        const urls = (signed.data && signed.data.data && signed.data.data.urls) || {};
        const url = urls[partNumber];
        if (!url) throw new Error(`Could not get an upload URL for part ${partNumber}`);
        return url;
      };

      const putPartWithRetry = async (partNumber, blob, onBytes, attempt = 1) => {
        try {
          const url = await signPart(partNumber);
          return await putPart(url, blob, onBytes);
        } catch (err) {
          // Three attempts per part. Retrying a 64MB chunk is cheap; retrying a
          // 1.5GB file is not, which is the whole point of chunking.
          if (attempt < 3) {
            onBytes(0);
            return putPartWithRetry(partNumber, blob, onBytes, attempt + 1);
          }
          throw err;
        }
      };

      try {
        // Bytes confirmed for parts that have finished, so an in-flight part's
        // progress can be added on top without double counting.
        let settledBytes = 0;
        const inFlight = {};

        const publish = () => {
          const live = Object.keys(inFlight).reduce((a, k) => a + inFlight[k], 0);
          loadedBytes[i] = settledBytes + live;
          reportProgress();
          if (typeof onFileProgress === "function") {
            onFileProgress({
              index: i,
              total: items.length,
              name: file.name,
              percent: Math.min(100, Math.round((loadedBytes[i] / (file.size || 1)) * 100))
            });
          }
        };

        const parts = new Array(partCount);
        let cursor = 0;

        // Three parts in flight: enough to saturate a typical uplink without
        // opening so many sockets that progress becomes unreadable.
        const worker = async () => {
          while (cursor < partCount) {
            const idx = cursor;
            cursor += 1;

            const partNumber = idx + 1;
            const start = idx * partSize;
            const blob = file.slice(start, Math.min(start + partSize, file.size));

            inFlight[partNumber] = 0;
            const etag = await putPartWithRetry(
              partNumber,
              blob,
              bytes => {
                inFlight[partNumber] = bytes;
                publish();
              }
            );

            delete inFlight[partNumber];
            settledBytes += blob.size;
            parts[idx] = { PartNumber: partNumber, ETag: etag };
            publish();
          }
        };

        await Promise.all(
          Array.from({ length: Math.min(3, partCount) }, () => worker())
        );

        const done = await axios.post("/v1/mtc_lab/upload/multipart/complete", {
          kit_id,
          key,
          upload_id: uploadId,
          parts,
          file_name: file.name,
          file_type: meta.file_type,
          sample_index: meta.sample_index
        });

        loadedBytes[i] = file.size || 0;
        reportProgress();
        return done;
      } catch (err) {
        // Don't leave orphaned parts accruing storage cost in the Space.
        axios
          .post("/v1/mtc_lab/upload/multipart/abort", { key, upload_id: uploadId })
          .catch(() => {});
        throw err;
      }
    };

    // Files sequentially, parts concurrently within each file.
    let chain = Promise.resolve();
    items.forEach((item, i) => {
      chain = chain.then(() => uploadOne(item, i));
    });

    return chain
      .then(() => axios.post("/v1/mtc_lab/upload/commit", { kit_id }))
      .then(res => {
        const data = (res.data && res.data.data) || {};
        const uploadedFiles = data.files || [];
        commit("setMtcLabFiles", uploadedFiles);
        return {
          files: uploadedFiles,
          kit_status: data.kit_status,
          lab_stage: data.lab_stage,
          status_changed: data.status_changed
        };
      });
  },
  getMtcLabFiles({ commit }, kit_id) {
    return axios.get(`/v1/mtc_lab/files?kit_id=${kit_id}`).then(res => {
      commit("setMtcLabFiles", res.data.data.files);
      return res.data.data.files;
    });
  },
  deleteMtcLabFile(_, id) {
    return axios.delete(`/v1/mtc_lab/files/${id}`).then(res => {
      return res.data && res.data.data;
    });
  },
  downloadMtcLabFile(_, file) {
    return axios
      .get(`/v1/mtc_lab/files/${file.id}/download`, { responseType: "blob" })
      .then(res => {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/octet-stream"
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.file_name || `file_${file.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return true;
      });
  },
  searchMtcLabKit({ commit }, kitId) {
    return axios.get(`/v1/admin/kitsv2Labs?search=${kitId}`).then(res => {
      const kits = res.data.data;
      if (kits && kits.length > 0) {
        return kits[0];
      }
      return Promise.reject(new Error("Kit not found"));
    });
  },
  // Lab Work Queue: V2 kits grouped into lab buckets.
  fetchWorkQueue({ commit }) {
    return axios.get("/v1/mtc_lab/work-queue").then(res => {
      commit("setWorkQueue", res.data.data);
      return res.data.data;
    });
  },
  // Lab user advances a V2 kit through the internal lab workflow. Send an
  // `action` (accept / start_dna_extraction / start_sequencing); a legacy `status`
  // is still supported for the admin/manual fallback path.
  updateMtcLabKitStatus(_, { kit_id, action, status }) {
    const id = String(kit_id).replace(/[-\s]/g, "");
    const payload = action ? { action } : { status };
    return axios
      .put(`/v1/mtc_lab/kits/${id}/status`, payload)
      .then(res => res.data && res.data.data);
  }
};

const getters = {
  getMtcLabUsers() {
    return state.mtcLabUsers;
  },
  getMtcLabFiles() {
    return state.mtcLabFiles;
  },
  getMtcLabPagination() {
    return state.mtcLabPagination;
  },
  getWorkQueue() {
    return state.workQueue;
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};
